<template>
    <CqModal :showModal="show" @closeModal="closeModal('CANCEL')" heading="Rooster publiceren">
        <div>Rooster publiceren t/m {{ getFormattedDate() }}. Deze actie kan niet ongedaan worden gemaakt.</div>
        <div v-if="rosterHasDeriveRightsUntilEnabled" class="date-select-wrapper">
            <label>Rooster definitief t/m:</label>
            <CustomDateSelect
                @update:modelValue="setDeriveRightsUntilDate"
                :modelValue="deriveRightsUntilDate"
                :min="minDeriveRightsUntilDate ?? undefined"
                :max="newPublishedUntilDate ?? undefined"
            />
            <p>Medewerkers kunnen rechten ontlenen aan het rooster tot en met de datum dat dat definitief is.</p>
        </div>
        <CqButtonBar>
            <CqButton label="Annuleren" @click="closeModal('CANCEL')" />
            <CqButton primary label="Bevestigen" @click="closeModal('OK', deriveRightsUntilDate)" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqModal} from '@cquential/ui';
    import CustomDateSelect from '@/components/Branding/CustomDateSelect.vue';
    import {RosterData, RosterPublishConfirmData} from '@/types/types';
    import Dates from '@cquential/date-utils';
    import {Component, Vue} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CustomDateSelect,
        },
    })
    export default class PublishRosterModal extends Vue {
        private static instance: PublishRosterModal | null = null;

        private show: boolean = false;
        private minDeriveRightsUntilDate: Date | null = null;
        private newPublishedUntilDate: Date | null = null;
        private deriveRightsUntilDate: Date | undefined = undefined;
        private rosterHasDeriveRightsUntilEnabled: boolean = false;
        private resolve: ((result: RosterPublishConfirmData) => void) | undefined = undefined;

        public mounted(): void {
            PublishRosterModal.instance = this;
        }

        public beforeUnmount(): void {
            PublishRosterModal.instance = null;
        }

        public openModal(roster: RosterData, openNewPublishedUntilDate: Date, openRosterHasDeriveRightsUntilEnabled: boolean = false): Promise<RosterPublishConfirmData> {
            this.newPublishedUntilDate = openNewPublishedUntilDate;

            if (openRosterHasDeriveRightsUntilEnabled) {
                if (roster.publishedUntil) {
                    this.newPublishedUntilDate = Dates.Utils.max(openNewPublishedUntilDate, roster.publishedUntil);
                }

                this.rosterHasDeriveRightsUntilEnabled = openRosterHasDeriveRightsUntilEnabled;
                this.minDeriveRightsUntilDate = roster.deriveRightsUntil ?? roster.beginDate ?? new Date();
                this.deriveRightsUntilDate = this.newPublishedUntilDate;
            }

            this.show = true;

            return new Promise<RosterPublishConfirmData>((openResolve: (result: RosterPublishConfirmData) => void): void => {
                this.resolve = openResolve;
            });
        }

        public setDeriveRightsUntilDate(date: Date | undefined): void {
            this.deriveRightsUntilDate = date;
        }

        public closeModal(result: 'CANCEL' | 'OK', deriveRightsUntil?: Date): void {
            const data: RosterPublishConfirmData = {
                result: result,
                deriveRightsUntil: deriveRightsUntil,
            };
            this.resolve!(data);
            this.rosterHasDeriveRightsUntilEnabled = false;
            this.minDeriveRightsUntilDate = null;
            this.deriveRightsUntilDate = undefined;
            this.show = false;
        }

        public getFormattedDate(): string {
            if (this.newPublishedUntilDate) {
                return Dates.Formatter.formatDateLong(this.newPublishedUntilDate);
            }

            return '';
        }

        public static openModal(roster: RosterData, newPublishedUntilDate: Date, rosterHasDeriveRightsUntilEnabled: boolean = false): Promise<RosterPublishConfirmData> {
            return this.instance!.openModal(roster, newPublishedUntilDate, rosterHasDeriveRightsUntilEnabled);
        }
    }
</script>

<style lang="scss" scoped>
    .date-select-wrapper {
        margin: 2rem 0;
    }
</style>
